import { CARD_COLORS, CARD_COLUMNS } from '../data.js'

export const uid = (prefix = 'id') => prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7)

export function charCount(text = '') {
  const cjk = (text.match(/[\u3400-\u9fff]/g) || []).length
  const words = (text.match(/[A-Za-z0-9]+/g) || []).length
  return cjk + words
}

export function visibleSnippet(text = '', max = 92) {
  return text.replace(/\s+/g, ' ').trim().slice(0, max)
}

export function firstSentence(text = '') {
  const cleaned = text.replace(/\s+/g, ' ').trim()
  const match = cleaned.match(/[^。！？!?]+[。！？!?]?/)
  return (match ? match[0] : cleaned).trim()
}

export function paragraphs(text = '') {
  return text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
}

export function summaryFromContent(content = '', fallback = '一张等待被写完的卡片。') {
  const para = paragraphs(content)[0] || ''
  const sentence = firstSentence(para)
  return sentence ? sentence.slice(0, 78) : fallback
}

export function columnMeta(id) {
  if (id === 'unsorted') return { id: 'unsorted', label: '待定', color: '#a99f8a', en: 'HOLD' }
  return CARD_COLUMNS.find((c) => c.id === id) || CARD_COLUMNS[0]
}

export function cardColor(card) {
  return CARD_COLORS[card.color] || CARD_COLORS.paper
}

const KIND_COLORS = { character: '#c98a63', outline: '#6f86a3', source: '#8a6fa3' }

export function cardTint(card) {
  return KIND_COLORS[card.kind] || cardColor(card)
}

export function projectProgress(project) {
  const weights = { inspiration: 0.12, draft: 0.42, polish: 0.74, done: 1 }
  if (!project.cards.length) return 0
  const total = project.cards.reduce((sum, card) => sum + (weights[card.column] ?? 0), 0)
  return Math.round((total / project.cards.length) * 100)
}

export function projectWords(project) {
  return project.cards.reduce((sum, card) => sum + charCount(card.content), 0)
}

export function relativeTime(iso) {
  if (!iso) return '刚刚'
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return '刚刚'
  if (minutes < 60) return minutes + ' 分钟前'
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return hours + ' 小时前'
  const days = Math.floor(hours / 24)
  if (days < 7) return days + ' 天前'
  const date = new Date(iso)
  return (date.getMonth() + 1) + ' 月 ' + date.getDate() + ' 日'
}

export function fullDate(date = new Date()) {
  return new Intl.DateTimeFormat('zh-CN', { month: 'long', day: 'numeric', weekday: 'long' }).format(date)
}

export function mergeUnique(list) {
  return Array.from(new Set(list.filter(Boolean)))
}

export function splitIntoBlocks(content = '') {
  let blocks = content.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
  if (blocks.length <= 1) {
    const raw = content.trim()
    const sentences = raw.match(/[^。！？!?]+[。！？!?]?/g) || (raw ? [raw] : [])
    blocks = sentences.map((s) => s.trim()).filter(Boolean)
  }
  return blocks
}

export function nextGraphPosition(cards) {
  const index = cards.length
  return { x: 0.18 + (index % 4) * 0.2, y: 0.22 + Math.floor(index / 4) * 0.26 }
}

export function sortCardsByOrder(cards) {
  return cards.slice().sort((a, b) => {
    const ai = cards.indexOf(a)
    return ai
  })
}

export function makeStarterProject(input) {
  const id = uid('notebook')
  const now = new Date().toISOString()
  const title = input.title.trim() || '未命名笔记本'
  const type = input.type || '长篇小说'
  const theme = input.theme || 'forest'
  const cards = [
    {
      id: uid('card'),
      title: '从这里开始',
      summary: '写下第一个念头，不必完整。它会和之后的卡片一起长成结构。',
      content: '写下第一个念头，不必完整。\n\n可以是一个人、一句对话、一个还没有解释的物件。之后的某一天，你会在另一张卡片上再次遇见它。',
      column: 'inspiration',
      act: '第一幕 · 未命名',
      tags: ['开场'],
      color: 'sand',
      graph: { x: 0.3, y: 0.32 },
      notes: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: uid('card'),
      title: '一个人物',
      summary: '他想要什么？他害怕被谁看见？',
      content: '人物卡：\n\n他想要：\n他真正需要：\n他最害怕被谁看见：\n\n把这三个答案写得越具体，故事就越难走偏。',
      column: 'inspiration',
      act: '第一幕 · 未命名',
      tags: ['人物'],
      color: 'sage',
      graph: { x: 0.62, y: 0.58 },
      notes: '',
      createdAt: now,
      updatedAt: now,
    },
  ]
  return {
    id,
    title,
    subtitle: input.subtitle.trim() || '一本刚刚开始的笔记本',
    type,
    theme,
    description: '这里的每一张卡片都可以被拆开、合并，也可以拉到看板上与另一张卡片发生关系。',
    goalWords: 3000,
    createdAt: now,
    updatedAt: now,
    cards,
    connections: [],
  }
}

export function buildManuscript(project, cards = project.cards) {
  const sections = []
  cards.forEach((card) => {
    const last = sections[sections.length - 1]
    if (!last || last.act !== card.act) {
      sections.push({ act: card.act || '未分幕', cards: [card] })
    } else {
      last.cards.push(card)
    }
  })
  const words = cards.reduce((sum, card) => sum + charCount(card.content), 0)
  const lines = ['# ' + project.title, '', '> ' + project.subtitle, '', '---', '']
  sections.forEach((section) => {
    lines.push('## ' + section.act, '')
    section.cards.forEach((card) => {
      lines.push('### ' + card.title, '')
      lines.push(card.content.trim() || '（待写）', '')
    })
  })
  lines.push('---', '', '由页屿 · 收束工作室整理')
  return { sections, words, count: cards.length, markdown: lines.join('\n') }
}

export function downloadText(filename, content) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export function safeFilename(name = '手稿') {
  return name.replace(/[\\/:*?"<>|]/g, '-')
}
